import { useCallback, useMemo, useState } from "react";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { useMutation } from "@tanstack/react-query";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { type SelectDocumentsStep } from "@hsl/fc-subscription/types";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { Modal } from "@hsl/lgim-components/components";

import useFundData from "../../../../../hooks/useFundData";
import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import {
    SubscribableAssetsService,
    type SubscribableAsset,
    type SubscribableAssetCreate,
    type SubscribableAssetUpdate,
} from "../../../../../openapi";
import useExplorerStore from "../../../../../store/store";
import Button from "../../../../Button";
import { TextSkeleton } from "../../../../Skeletons";
import Spinner from "../../../../Spinner";
import {
    SubscribeIcon,
    WatchIcon,
} from "../../../SubscriptionButtons/SubscriptionButtons";
import {
    getAvailableDocuments,
    SelectDocumentsContext,
    useDocumentStructure,
} from "../../../SubscriptionFlows/Steps/SelectDocuments/context";
import {
    createSubscriptionsHelper,
    updateSubscriptionsHelper,
} from "../../../SubscriptionFlows/Steps/SelectDocuments/helpers";
import SelectDocumentsCollapsible from "../../../SubscriptionFlows/Steps/SelectDocuments/SelectDocumentsCollapsible";
import { type SelectDocumentContext } from "../../../SubscriptionFlows/Steps/SelectDocuments/types";

interface Props {
    fundId: string;
    subscriptions: Required<SubscribableAsset>[];
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
}

export const FundRow = ({
    subscriptions,
    fundId,
    expanded,
    setExpanded,
}: Props) => {
    const { userId } = useFCSubscriptionStore((state) => state.user!);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const refreshSubscriptions = useFCSubscriptionStore(
        (state) => state.refreshUserSubscriptions,
    );
    const { buttonClickEvent } = useYardActionEvents();
    const T = useTranslation();
    const [subscriptionsBeingEdited, setSubscriptions] = useState<
        SubscribableAssetCreate[]
    >(() => {
        return subscriptions
            .filter((x) => x.subscriptionType === "FULL")
            .map(
                (sub) =>
                    ({
                        subscriptionId: sub.subscriptionId,
                        subscriptionType: "FULL",
                        fundId: sub.fundId,
                        literatureSetCode: sub.literatureSetCode,
                        subscriptionLevel: sub.subscriptionLevel,
                        entityId: sub.entityId,
                        frequency: sub.frequency,
                    }) satisfies Required<SubscribableAssetCreate>,
            );
    });

    const locale = useExplorerStore((state) => state.config.locale);
    const fund = useFundData(`fund-${fundId}`);

    const query = useDocumentStructure(fund?.data.share_classes);

    const formatter = useMemo(
        () =>
            new Intl.DateTimeFormat([locale.replace("_", "-"), "en-GB"], {
                year: "numeric",
                month: "short",
                day: "numeric",
            }),
        [locale],
    );

    const { mutate: deleteSubscriptions, isLoading: isDeleting } = useMutation({
        mutationFn: async (subs: string[]) => {
            await SubscribableAssetsService.deleteSubscriptions(userId, subs);
            await refreshSubscriptions();
        },
    });

    const { mutate: saveSubscriptions, isLoading: isSaving } = useMutation({
        mutationFn: async (subs: SubscribableAssetCreate[]) => {
            const subsToDelete: string[] = [];
            const subsToCreate: SubscribableAssetCreate[] = [];
            const subsToUpdate: SubscribableAssetUpdate[] = [];

            // edit or remove existing subscriptions
            for (const subscription of subs) {
                const existingSub = subscriptions.find(
                    (x) => x.subscriptionId === subscription.subscriptionId,
                );
                if (existingSub) {
                    if (existingSub.frequency !== subscription.frequency) {
                        subsToUpdate.push({
                            subscriptionId: subscription.subscriptionId,
                            frequency: subscription.frequency,
                            subscriptionStatus: "CONFIRMED",
                            subscriptionType: "FULL",
                        });
                    }
                } else {
                    subsToCreate.push(subscription);
                }
            }

            // remove subscriptions that are no longer selected
            for (const subscription of subscriptions.filter(
                (x) => x.subscriptionType === "FULL",
            )) {
                if (
                    !subs.find(
                        (x) => x.subscriptionId === subscription.subscriptionId,
                    )
                ) {
                    subsToDelete.push(subscription.subscriptionId);
                }
            }

            // send API requests all at once

            const promises = [];

            if (subsToDelete.length > 0) {
                promises.push(
                    SubscribableAssetsService.deleteSubscriptions(
                        userId,
                        subsToDelete,
                    ),
                );
            }

            if (subsToCreate.length > 0) {
                promises.push(
                    SubscribableAssetsService.createSubscriptions(
                        userId,
                        subsToCreate,
                    ),
                );
            }

            if (subsToUpdate.length > 0) {
                promises.push(
                    SubscribableAssetsService.updateSubscriptions(
                        userId,
                        subsToUpdate,
                    ),
                );
            }

            await Promise.all(promises);

            await refreshSubscriptions();
        },
        onSuccess: () => {
            setExpanded(false);
        },
    });

    const createSubscriptions: SelectDocumentContext["createSubscriptions"] =
        useCallback(
            (doc, restrict) => {
                if (!fund) throw new Error("Fund not found");
                return createSubscriptionsHelper(doc, restrict, "FULL", [fund]);
            },
            [fund],
        );

    const updateSelection: SelectDocumentContext["updateSelection"] =
        useCallback(
            (args) => {
                setSubscriptions((subs) => {
                    const updated = updateSubscriptionsHelper(
                        subs,
                        "MONDAY",
                        [fund!],
                        args,
                    );
                    return updated.data?.subscriptions ?? subs;
                });
            },
            [fund],
        );

    if (!fund) {
        console.log("Fund not found", fundId);
        return null;
    }

    const mostRecentSubscriptionDate = subscriptions
        .map((sub) => sub.createdDateTime)
        .filter(Boolean)
        .map((date) => new Date(date))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    const watching = subscriptions.some(
        (sub) => sub.subscriptionType === "WATCHLIST",
    );
    const subscribed = subscriptions.some(
        (sub) => sub.subscriptionType === "FULL",
    );

    return (
        <>
            <tr
                className={cx(
                    "border-platinum grid-cols-[32px_1fr_32px] flex-col transition-opacity max-md:mt-4 max-md:grid max-md:border [&>td]:px-5 [&>td]:py-2.5",
                    isDeleting && "pointer-events-none opacity-50",
                    expanded
                        ? "md:border-l-dark-blue md:border-r-platinum md:border-l-[6px] md:border-r md:bg-white/60"
                        : "md:border-b",
                )}
            >
                <td
                    className={cx(
                        "col-span-3 w-full",
                        expanded && "border-dark-blue border-l-[6px]",
                    )}
                >
                    <div>
                        <h3 className="text-dark-blue text-lg font-medium">
                            {fund.data.data.name}
                        </h3>
                        {subscribed &&
                            (query.isLoading ? (
                                <TextSkeleton width={32} className="text-sm" />
                            ) : (
                                <p className="text-taupe-grey line-clamp-1 text-sm">
                                    {Array.from(
                                        new Set(
                                            subscriptions
                                                .map(
                                                    (sub) =>
                                                        query.data
                                                            ?.find((x) =>
                                                                x.subscription_data.subsMap?.some(
                                                                    (y) =>
                                                                        y[2] ===
                                                                        sub.subscriptionId,
                                                                ),
                                                            )
                                                            // remove stuff in brackets
                                                            ?.display_name.replace(
                                                                /\([^)]*\)/g,
                                                                "",
                                                            )
                                                            .trim() ?? "",
                                                )
                                                .filter(Boolean),
                                        ),
                                    ).join(", ")}
                                </p>
                            ))}
                    </div>
                </td>
                <td
                    className={cx(
                        "max-md:w-16",
                        expanded &&
                            "max-md:border-dark-blue max-md:border-l-[6px]",
                    )}
                >
                    <WatchIcon
                        className="mx-auto h-5 w-5 max-md:mt-px md:h-4 md:w-4"
                        active={watching}
                    />
                </td>
                <td>
                    <button
                        type="button"
                        aria-description="Edit subscription"
                        onClick={() => setExpanded(true)}
                        className="flex w-max flex-row items-center gap-1 pr-0.5 md:mx-auto"
                    >
                        <SubscribeIcon
                            className="h-5 w-5 md:h-4 md:w-4"
                            active={subscribed}
                        />
                        <span className="md:text-sm">
                            <Translate>
                                {subscribed ? "Edit" : "Subscribe"}
                            </Translate>
                        </span>
                    </button>
                </td>
                <td className="text-right max-md:hidden">
                    {mostRecentSubscriptionDate && (
                        <time
                            dateTime={mostRecentSubscriptionDate.toISOString()}
                            className="inline-block w-max"
                        >
                            {formatter.format(mostRecentSubscriptionDate)}
                        </time>
                    )}
                </td>
                <td className="max-md:-ml-6 max-md:w-16">
                    <button
                        aria-description="Delete subscription"
                        onClick={() => {
                            buttonClickEvent({
                                name: "Delete",
                                position: "Subscription listings",
                                details: fund.data.data.name,
                                fund: {
                                    id: fund.data.id,
                                    type: fund.type,
                                    name: fund.data.data.name,
                                },
                            });
                            setConfirmRemove(true);
                        }}
                    >
                        <TrashIcon className="h-5 w-5 max-md:mt-px md:h-4 md:w-4" />
                    </button>
                </td>
            </tr>
            <CollapsibleRow
                show={expanded}
                subscribed={subscribed}
                fund={fund}
                subscriptions={subscriptionsBeingEdited}
                createSubscriptions={createSubscriptions}
                updateSelection={updateSelection}
                documentQuery={query}
                onCancel={() => {
                    setExpanded(false);
                    setSubscriptions(
                        subscriptions
                            .filter((x) => x.subscriptionType === "FULL")
                            .map(
                                (sub) =>
                                    ({
                                        subscriptionId: sub.subscriptionId,
                                        subscriptionType: "FULL",
                                        fundId: sub.fundId,
                                        literatureSetCode:
                                            sub.literatureSetCode,
                                    }) satisfies SubscribableAssetCreate,
                            ),
                    );
                }}
                onRemoveSubscription={() => {
                    const subsToDelete = subscriptions
                        .filter((x) => x.subscriptionType === "FULL")
                        .map((x) => x.subscriptionId);
                    deleteSubscriptions(subsToDelete);
                    setExpanded(false);
                    buttonClickEvent({
                        name: "Unsubscribe",
                        position: "Document subscription options",
                        details: fund.data.data.name,
                    });
                }}
                onSave={() => {
                    saveSubscriptions(subscriptionsBeingEdited);
                    buttonClickEvent({
                        name: "Save",
                        position: "Document subscription options",
                        details: fund.data.data.name,
                    });
                }}
                isSaving={isSaving}
            />
            <Modal
                open={confirmRemove}
                onClose={() => setConfirmRemove(false)}
                title={T("Remove fund").result}
            >
                <div className="p-8 text-left">
                    <p className="text-charcoal text-base">
                        <Translate insert={fund.data.data.name}>
                            You are about to remove the
                        </Translate>
                        .
                    </p>
                </div>
                <div className="flex gap-4 p-8">
                    <Button
                        onClick={() => {
                            setConfirmRemove(false);
                            setExpanded(false);
                            deleteSubscriptions(
                                subscriptions.map((sub) => sub.subscriptionId),
                            );
                        }}
                        color="primary"
                        variant="large"
                    >
                        <Translate>Remove</Translate>
                    </Button>
                    <Button
                        onClick={() => setConfirmRemove(false)}
                        color="white"
                        variant="large"
                    >
                        <Translate>Cancel</Translate>
                    </Button>
                </div>
            </Modal>
        </>
    );
};

const CollapsibleRow = ({
    show,
    fund,
    subscriptions,
    createSubscriptions,
    updateSelection,
    documentQuery: query,
    onCancel,
    onRemoveSubscription,
    subscribed,
    onSave,
    isSaving,
}: {
    show: boolean;
    fund: FundDataWithType;
    subscriptions: SubscribableAssetCreate[];
    createSubscriptions: SelectDocumentContext["createSubscriptions"];
    updateSelection: SelectDocumentContext["updateSelection"];
    documentQuery: ReturnType<typeof useDocumentStructure>;
    onCancel: () => void;
    onRemoveSubscription: () => void;
    subscribed: boolean;
    onSave: () => void;
    isSaving: boolean;
}) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const getShareClassById = useExplorerStore(
        (state) => state.getShareClassById,
    );
    const shareClasses = fund.data.share_classes
        .map((sc) => getShareClassById(`shareclass-${sc}`)!)
        .filter(Boolean);

    const contextValue = useMemo(
        (): SelectDocumentContext => ({
            updateSelection,
            createSubscriptions,
            stepData: {
                name: "select-documents",
                data: {
                    subscriptions,
                    dayOfWeek: "MONDAY",
                },
            } as SelectDocumentsStep,
            status: query.status,
            availableFunds: [fund],
            availableShareClasses: shareClasses,
            keyDocuments: getAvailableDocuments(query.data, "key"),
            allDocuments: getAvailableDocuments(query.data),
            subscriptionType: "FULL",
            applyToAllFunds: false,
            updateDayOfWeek: () => {},
        }),
        [
            createSubscriptions,
            subscriptions,
            updateSelection,
            fund,
            query.data,
            shareClasses,
            query.status,
        ],
    );

    const T = useTranslation();

    if (!show) return null;

    return (
        <>
            <tr className="border-platinum border-b border-r bg-white/60 transition-colors duration-200 max-md:ml-px max-md:block">
                <td colSpan={5} className="border-dark-blue border-l-[6px]">
                    <div className="w-full px-5 py-2.5">
                        {query.data ? (
                            <SelectDocumentsContext.Provider
                                value={contextValue}
                            >
                                <SelectDocumentsCollapsible
                                    funds={[fund]}
                                    shareClasses={shareClasses}
                                    docSubscriptionCreator={createSubscriptions}
                                    allDocuments={getAvailableDocuments(
                                        query.data,
                                    )}
                                    keyDocuments={getAvailableDocuments(
                                        query.data,
                                        "key",
                                    )}
                                    status="success"
                                    subscriptions={subscriptions}
                                    onUpdate={updateSelection}
                                />
                                <div className="my-4 flex items-center justify-between gap-4">
                                    <button
                                        className="text-core-blue text-base underline"
                                        onClick={onCancel}
                                    >
                                        <Translate>Cancel</Translate>
                                    </button>
                                    <div className="flex items-center gap-4">
                                        {subscribed && (
                                            <Button
                                                color="white"
                                                variant="large"
                                                onClick={() =>
                                                    setConfirmDelete(true)
                                                }
                                            >
                                                <span className="md:hidden">
                                                    <Translate>
                                                        Remove
                                                    </Translate>
                                                </span>
                                                <span className="max-md:hidden">
                                                    <Translate>
                                                        Remove subscription
                                                    </Translate>
                                                </span>
                                            </Button>
                                        )}
                                        <Button
                                            color="primary"
                                            variant="large"
                                            onClick={onSave}
                                            disabled={isSaving}
                                        >
                                            <Translate>Save</Translate>
                                        </Button>
                                    </div>
                                </div>
                            </SelectDocumentsContext.Provider>
                        ) : (
                            <Spinner />
                        )}
                    </div>
                </td>
            </tr>
            <Modal
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                title={T("Remove subscription").result}
            >
                <div className="p-8 text-left">
                    <p className="text-charcoal text-base">
                        <Translate insert={fund.data.data.name}>
                            You are about to remove your subscription to the
                        </Translate>
                        .
                    </p>
                </div>
                <div className="flex gap-4 p-8">
                    <Button
                        onClick={() => {
                            onRemoveSubscription();
                            setConfirmDelete(false);
                        }}
                        color="primary"
                        variant="large"
                    >
                        <Translate>Remove</Translate>
                    </Button>
                    <Button
                        onClick={() => setConfirmDelete(false)}
                        color="white"
                        variant="large"
                    >
                        <Translate>Cancel</Translate>
                    </Button>
                </div>
            </Modal>
        </>
    );
};
